import { useCallback } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { oneFieldAmount } from 'bl/fields.bl';
import Permitted from 'components/core/Permitted.component';
import CurrencySign from 'components/price/CurrencySign.component';
import PERM from 'constants/core/perm.const';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import { selectCashupApiData, selectCashupDepositLogs, selectCashupSoldVouchers } from 'selectors/cashup.selectors';
import getObject from 'utils/accessors/getObject.util';
import asArray from 'utils/arrays/asArray.util';
import sumArray from 'utils/arrays/sumArray.util';
import fromAnyToCoinStr from 'utils/converters/any/fromAnyToCoinStr.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromEventToValue } from 'utils/converters/event.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import asObject from 'utils/objects/asObject.util';

import CN from './CashupTableLeft.module.scss';

const STYLE_LEFT = Object.freeze({ float: 'left' });

const DEFAULT_NUM = 0;
const DEFAULT_STR = '0.00';
const DEFAULT_COUNT = '';

const DISPLAY_BY_DAY = 'by-day';
const DISPLAY_BY_RANGE = 'by-range';

const bySourceOther = (item) => asObject(item).source === 'other';
const rounded = (number) => Math.round(number * 100) / 100;

const determineCardMachineTotal = ({ byRangeModal, total, reconcileList }) => {
  if (byRangeModal === DISPLAY_BY_DAY) {
    return total > 0 ? total : '';
  }

  const reconcileTotal = asObject(reconcileList).total_salonCardReceipts;

  if (typeof reconcileTotal === 'number') {
    return reconcileTotal;
  }

  return void 1;
};

const SimpleRow = ({ value, isPrice, children }) => (
  <tr data-bem="SimpleRow">
    <td>
      <div>{children}</div>
    </td>
    <td className={CN.lastCell}>
      <span className={cn({ price: isPrice })}>
        <CurrencySign value={value} />
      </span>
    </td>
  </tr>
);

const VoucherHeader = ({ t }) => (
  <tr data-bem="VoucherHeader">
    <td className={CN.bold}>{t('cashUp.cashupTableLeft.tableHeader.soldVauchers')}</td>
    <td className={CN.headerText}>{t('cashUp.cashupTableLeft.tableHeader.sold')}</td>
    <td className={cn(CN.lastCell, CN.bold)}>{t('cashUp.cashupTableLeft.tableHeader.netValue')}</td>
  </tr>
);

const VoucherRow = ({ title, count, netValue, isTotal }) => (
  <tr data-bem="VoucherRow" className={isTotal && CN.totalRow}>
    <td>
      <div className={CN.cellGap}>{title}</div>
    </td>
    <td className={CN.textRight}>{count}</td>
    <td className={CN.lastCell}>
      <CurrencySign value={netValue} />
    </td>
  </tr>
);

const DepositHeader = ({ t }) => (
  <tr data-bem="DepositHeader">
    <td>{t('cashUp.cashupTableLeft.tableHeader.depositTaken')}</td>
    <td className={CN.headerText}>{t('cashUp.cashupTableLeft.tableHeader.deposited')}</td>
    <td className={CN.headerText}>{t('cashUp.cashupTableLeft.tableHeader.refunded')}</td>
    <td className={CN.lastCell}>{t('cashUp.cashupTableLeft.tableHeader.netClientAmount')}</td>
  </tr>
);

const DepositRow = ({ title, depositCount, refundedCount, netValue, isTotal }) => (
  <tr data-bem="DepositRow" className={isTotal && CN.totalRow}>
    <td>
      <div className={CN.cellGap}>{title}</div>
    </td>
    <td className={CN.textRight}>{depositCount}</td>
    <td className={CN.textRight}>{refundedCount}</td>
    <td className={CN.lastCell}>
      <CurrencySign value={netValue} />
    </td>
  </tr>
);

const ContainmentTable = ({ id, children, tag }) => (
  <div
    data-bem="ContainmentTable"
    data-tag={fromAnyToTrimmed(tag)}
    data-id={fromAnyToTrimmed(id)}
    className="tbl-shadow-container cfix"
    id={id}
  >
    <table className="csh-table">
      <tbody>{children}</tbody>
    </table>
  </div>
);

const TotalTakings = ({ value, t }) => (
  <tr data-bem="TotalTakings">
    <td>
      <span className="text-bold upper" style={STYLE_LEFT}>
        {t('cashUp.cashupTableLeft.tableHeader.totalPayments')}
      </span>
      <div className="info display-left">
        <span className="tooltip">
          <i>{t('cashUp.cashupTableLeft.tooltipTotalPaymentsPart1')}</i>
          <br /> <br />
          <i>{t('cashUp.cashupTableLeft.tooltipTotalPaymentsPart2')}</i>
        </span>
      </div>
    </td>
    <td>
      <span className="price text-bold">
        <CurrencySign value={value} />
      </span>
    </td>
  </tr>
);

const CashupTableLeft = (props) => {
  const {
    byRangeModal,
    isSelfEmployedStaff,
    isStylist,
    singleStylistData,
    reconcileList,
    apiData,
    cashupData,
    depositsLogs,
    updateData,
  } = props;
  const { t } = useTranslation();

  const onInputChange = useCallback(
    (event) => {
      const value = fromEventToValue(event) || 0;

      if (Number.isNaN(value && value !== '.')) {
        return;
      }

      const updatedCashupData = {
        ...cashupData,
        cashReceiptTotal: value,
      };

      updateData(updatedCashupData);
    },
    [cashupData, updateData],
  );

  const {
    card: cVoucherCard,
    cash: cVoucherCash,
    cash_count: cVoucherCashCount,
    other: cVoucherOther,
    other_count: cVoucherOtherCount,
    total_count: cVoucherTotalCount,
    online: cVoucherOnline,
    online_count: cVoucherOnlineCount,
    slick_pos_card: cVoucherSlickPosCard,
    slick_pos_card_count: cVoucherSlickPosCardCount,
    non_slick_pos_card: cVoucherMonSlickPosCard,
    non_slick_pos_card_count: cVoucherMonSlickPosCardCount,
  } = getObject('cashupData.vouchers_amount', props);

  const {
    cash: dcDepositCash,
    cash_deposited_count: cDepositCashCount,
    cash_refunded_count: cRefundCashCount,
    online: dcDepositOnlineCard,
    online_deposited_count: cDepositOnlineCount,
    online_refunded_count: cRefundOnlineCount,
    total: cDepositTotal,
    total_deposited_count: cDepositTotalCount,
    total_refunded_count: cRefundTotalCount,
    other: cDepositOther,
    other_deposited_count: cDepositOtherCount,
    other_refunded_count: cRefundOtherCount,
    slick_pos_card: cDepositSlickPosCard,
    non_slick_pos_card: cDepositNonSlickPosCard,
    non_slick_pos_card_deposited_count: cDepositNonSlickPosCardDepositedCount,
    non_slick_pos_card_refunded_count: cDepositNonSlickPosCardRefundedCount,
    slick_pos_card_deposited_count: cDepositSlickPosCardDepositedCount,
    slick_pos_card_refunded_count: cRefundSlickPosCardCount,
  } = getObject('cashupData.deposits_amount', props);

  const {
    totalCash: aTotalCash,
    totalOnlineCard: aTotalOnlineCard,
    totalTip: aTotalTip,
    totalOther: aTotalOther,
    totalSalonCardSlickPOS: aTotalSalonCardSlickPOS,
    totalSalonCardNonSlickPOS: aTotalSalonCardNonSlickPOS,
  } = asObject(apiData);

  const {
    totalCash: cTotalCash,
    totalOnlineCard: cTotalOnlineCard,
    totalOther: cTotalOther,
    totalTakings: cTotalTakings,
    totalTip: cTotalTip,
    cashReceiptTotal: cCashReceiptTotal,
  } = asObject(cashupData);

  const cardMachineTotal = determineCardMachineTotal({ byRangeModal, total: cCashReceiptTotal, reconcileList });

  const dcDepositOther = sumArray(asArray(depositsLogs).filter(bySourceOther).map(oneFieldAmount));

  // cashupData display refunded count

  const dcRefundCashCount = fromAnyToString(cRefundCashCount) || DEFAULT_COUNT;
  const dcRefundCardCount = fromAnyToString(cRefundSlickPosCardCount) || DEFAULT_COUNT;
  const dcRefundTotalCount = fromAnyToString(cRefundTotalCount) || DEFAULT_COUNT;
  const dcRefundOtherCount = fromAnyToString(cRefundOtherCount) || DEFAULT_COUNT;
  const dcRefundOnlineCount = fromAnyToString(cRefundOnlineCount) || DEFAULT_COUNT;

  // cashupData display deposit count
  const dcDepositTotalCount = fromAnyToString(cDepositTotalCount) || DEFAULT_COUNT;
  const dcDepositOtherCount = fromAnyToString(cDepositOtherCount) || DEFAULT_COUNT;
  const dcDepositOnlineCount = fromAnyToString(cDepositOnlineCount) || DEFAULT_COUNT;
  const dcDepositCashCount = fromAnyToString(cDepositCashCount) || DEFAULT_COUNT;
  const dcDepositCardCount = fromAnyToString(cDepositSlickPosCardDepositedCount) || DEFAULT_COUNT;
  const dcDepositNonSlickPosCarCount = fromAnyToString(cDepositNonSlickPosCardDepositedCount) || DEFAULT_COUNT;
  const dcDepositNonSlickPosCarRefundedCount = fromAnyToString(cDepositNonSlickPosCardRefundedCount) || DEFAULT_COUNT;
  const dcDepositNonSlickPosCard = fromAnyToString(cDepositNonSlickPosCard) || DEFAULT_COUNT;

  // cashupData display voucher count
  const dcVoucherTotalCount = fromAnyToString(cVoucherTotalCount) || DEFAULT_COUNT;
  const dcVoucherCard = fromAnyToString(cVoucherSlickPosCard) || DEFAULT_COUNT;
  const dcVoucherCardCount = fromAnyToString(cVoucherSlickPosCardCount) || DEFAULT_COUNT;
  const dcVoucherCashCount = fromAnyToString(cVoucherCashCount) || DEFAULT_COUNT;
  const dcVoucherOnlineCount = fromAnyToString(cVoucherOnlineCount) || DEFAULT_COUNT;
  const dcVoucherOtherCount = fromAnyToString(cVoucherOtherCount) || DEFAULT_COUNT;
  const dcVoucherNonSlickPosCard = fromAnyToString(cVoucherMonSlickPosCard) || DEFAULT_COUNT;
  const dcVoucherNonSlickPosCardCount = fromAnyToString(cVoucherMonSlickPosCardCount) || DEFAULT_COUNT;

  // cashupData display voucher values
  const dcDepositTaken = fromAnyToCoinStr(rounded(cDepositTotal)) || DEFAULT_NUM;
  const dcVouchersCash = fromAnyToCoinStr(cVoucherCash) || DEFAULT_STR;
  const dcVouchersOnline = fromAnyToCoinStr(cVoucherOnline) || DEFAULT_STR;
  const dcTotalTip = fromAnyToCoinStr(cTotalTip) || DEFAULT_STR;
  const dcTotalOnlineCard = fromAnyToCoinStr(
    cTotalOnlineCard + (dcDepositOnlineCard || DEFAULT_NUM) + (cVoucherOnline || DEFAULT_NUM),
  );
  const dSumVoucherNetValueAll = fromAnyToCoinStr(cVoucherOther + cVoucherOnline + cVoucherCard + cVoucherCash);
  const dcTotalTakings = fromAnyToCoinStr(rounded(cTotalTakings));
  const dcTotalCash = cTotalCash + (dcDepositCash || DEFAULT_NUM);
  const dcTotalSalonCardSlick = cDepositSlickPosCard + cVoucherSlickPosCard + aTotalSalonCardSlickPOS || DEFAULT_NUM;
  const dcTotalSalonCardNonSlick =
    cDepositNonSlickPosCard + cVoucherMonSlickPosCard + aTotalSalonCardNonSlickPOS || DEFAULT_NUM;
  const dcTotalSalonCardSlickPersonal = aTotalSalonCardSlickPOS + cVoucherSlickPosCard || DEFAULT_NUM;
  const dcTotalSalonCardNonSlickPersonal = aTotalSalonCardNonSlickPOS + cVoucherMonSlickPosCard || DEFAULT_NUM;

  // single stylist cashupData.vouchers_amount display values
  const daTotalTip = fromAnyToCoinStr(rounded(aTotalTip)) || DEFAULT_NUM;
  const daTotalOnlineCard = fromAnyToCoinStr(rounded(cVoucherOnline)) || DEFAULT_NUM;
  const daTotalCash = fromAnyToCoinStr(rounded(cVoucherCash)) || DEFAULT_NUM;
  const daTotalOther = fromAnyToCoinStr(rounded(cVoucherOther)) || DEFAULT_NUM;

  // total for all and filter by single stylist - cashupData
  const dSumVoucherNetValueSingleSty = fromAnyToCoinStr(cVoucherOnline + cVoucherCard + cVoucherCash + cVoucherOther);

  const dSumOther = cTotalOther + (cDepositOther || 0) + (cVoucherOther || 0);

  // display values in TOTAL TAKINGS by filtering
  const filterTotalCash = fromAnyToCoinStr(rounded(aTotalCash + cVoucherCash)) || DEFAULT_NUM;
  const filterTotalCart = fromAnyToCoinStr(rounded(aTotalSalonCardSlickPOS + cVoucherSlickPosCard)) || DEFAULT_NUM;
  const filterTotalOnline = fromAnyToCoinStr(rounded(aTotalOnlineCard + cVoucherOnline)) || DEFAULT_NUM;
  const filterTotalOther = fromAnyToCoinStr(rounded(aTotalOther + cVoucherOther)) || DEFAULT_NUM;

  return (
    <section data-bem="CashupTableLeft" className={CN.component}>
      {singleStylistData ? (
        <>
          <ContainmentTable>
            <VoucherHeader t={t} />
            <VoucherRow
              title={t('cashUp.cashupTableLeft.tableRow.cash')}
              count={dcVoucherCashCount}
              netValue={daTotalCash}
            />
            <VoucherRow
              title={t('cashUp.cashupTableLeft.tableRow.inStoreSlickCard')}
              count={dcVoucherCardCount}
              netValue={dcVoucherCard}
            />
            <VoucherRow
              title={t('cashUp.cashupTableLeft.tableRow.inStoreCard')}
              count={dcVoucherNonSlickPosCardCount}
              netValue={dcVoucherNonSlickPosCard}
            />
            <VoucherRow
              title={t('cashUp.cashupTableLeft.tableRow.onlineCard')}
              count={dcVoucherOnlineCount}
              netValue={daTotalOnlineCard}
            />
            <VoucherRow
              title={t('cashUp.cashupTableLeft.tableRow.other')}
              count={dcVoucherOtherCount}
              netValue={daTotalOther}
            />
            <VoucherRow
              title={t('common.total')}
              count={dcVoucherTotalCount}
              netValue={dSumVoucherNetValueSingleSty}
              isTotal={true}
            />
          </ContainmentTable>
          <div className={`csh-table-wrap clearfix tables ${isSelfEmployedStaff || ''}`}>
            <div className={isStylist || ''}>
              <div className="tbl-shadow-container cfix">
                <table className="csh-table">
                  <tbody>
                    <SimpleRow value={filterTotalCash}>
                      <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.cashPaymentsPart1')}</span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.cashPaymentsPart2')}
                    </SimpleRow>
                    <SimpleRow value={filterTotalCart}>
                      <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCardSlickPart1')}</span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                    </SimpleRow>
                    <SimpleRow value={dcTotalSalonCardNonSlickPersonal}>
                      <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCardPart1')}</span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                    </SimpleRow>
                    <SimpleRow value={filterTotalOnline}>
                      <span className={CN.textBold}>
                        {t('cashUp.cashupTableLeft.tableRow.totalOnlinePaymentsPart1')}
                      </span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.totalOnlinePaymentsPart2')}
                    </SimpleRow>
                    <SimpleRow value={filterTotalOther}>
                      <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.other')}</span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.totalOtherPart2')}
                    </SimpleRow>
                  </tbody>
                </table>
              </div>

              <ContainmentTable id="tblTips">
                <SimpleRow value={daTotalTip}>
                  <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalTipsPart1')}</span>{' '}
                  {t('cashUp.cashupTableLeft.tableRow.totalTipsPart2')}
                </SimpleRow>
              </ContainmentTable>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={`csh-table-wrap clearfix tables ${isSelfEmployedStaff || ''}`}>
            <div className={isStylist || ''}>
              <div className="tbl-shadow-container cfix">
                <table className="csh-table">
                  <tbody>
                    <Permitted tag="CashupTableLeft__total-takings" value={PERM.canViewDailyCashupAllStaff}>
                      <TotalTakings value={dcTotalTakings} t={t} />
                    </Permitted>
                    <SimpleRow value={dcTotalCash}>
                      <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCash')}</span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                    </SimpleRow>
                    <Permitted tag="CashupTableLeft__total-card-all" value={PERM.canViewDailyCashupAllStaff}>
                      <SimpleRow value={dcTotalSalonCardSlick}>
                        <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCardSlickPart1')}</span>{' '}
                        {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                      </SimpleRow>
                    </Permitted>
                    <Permitted tag="CashupTableLeft__total-card-own" value={PERM.canViewDailyCashupPersonal}>
                      <SimpleRow value={dcTotalSalonCardSlickPersonal}>
                        <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCardSlickPart1')}</span>{' '}
                        {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                      </SimpleRow>
                    </Permitted>
                    <Permitted tag="CashupTableLeft__total-card-non-slick-all" value={PERM.canViewDailyCashupAllStaff}>
                      <SimpleRow value={dcTotalSalonCardNonSlick}>
                        <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCardPart1')}</span>{' '}
                        {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                      </SimpleRow>
                    </Permitted>
                    <Permitted tag="CashupTableLeft__total-card-non-slick-own" value={PERM.canViewDailyCashupPersonal}>
                      <SimpleRow value={dcTotalSalonCardNonSlickPersonal}>
                        <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCardPart1')}</span>{' '}
                        {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                      </SimpleRow>
                    </Permitted>
                    <SimpleRow value={dcTotalOnlineCard}>
                      <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalCardOnline')}</span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.totalCardPart2')}
                    </SimpleRow>
                    <SimpleRow value={dSumOther}>
                      <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.other')}</span>{' '}
                      {t('cashUp.cashupTableLeft.tableRow.totalOtherPart2')}
                    </SimpleRow>
                    <Permitted tag="CashupTableLeft__card-total" value={PERM.canViewDailyCashupAllStaff}>
                      <tr data-bem="CashupTableLeft__card-total" id="cardTotal">
                        <td>
                          <span className={CN.textBold}>
                            {t('cashUp.cashupTableLeft.tableRow.totalCardMachinePart1')}
                          </span>{' '}
                          {t('cashUp.cashupTableLeft.tableRow.totalCardMachinePart2')}
                        </td>
                        <td>
                          {byRangeModal === DISPLAY_BY_DAY && (
                            <input
                              type="text"
                              placeholder={t('common.placeholder.enterAmount')}
                              value={cardMachineTotal}
                              onChange={onInputChange}
                            />
                          )}
                          {byRangeModal === DISPLAY_BY_RANGE && (
                            <span>
                              {cardMachineTotal && cardMachineTotal > 0 ? (
                                <CurrencySign value={cardMachineTotal} />
                              ) : (
                                S.ndash
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    </Permitted>
                  </tbody>
                </table>
              </div>
              <ContainmentTable id="tblTips">
                <SimpleRow value={dcTotalTip}>
                  <span className={CN.textBold}>{t('cashUp.cashupTableLeft.tableRow.totalTipsPart1')}</span>{' '}
                  {t('cashUp.cashupTableLeft.tableRow.totalTipsPart2')}
                </SimpleRow>
              </ContainmentTable>

              <Permitted value={PERM.canViewDailyCashupAllStaff}>
                <ContainmentTable id="tblDeposit">
                  <DepositHeader t={t} />
                  <DepositRow
                    title={t('cashUp.cashupTableLeft.tableRow.cash')}
                    depositCount={dcDepositCashCount}
                    refundedCount={dcRefundCashCount}
                    netValue={dcDepositCash}
                  />
                  <DepositRow
                    title={t('cashUp.cashupTableLeft.tableRow.inStoreSlickCard')}
                    depositCount={dcDepositCardCount}
                    refundedCount={dcRefundCardCount}
                    netValue={cDepositSlickPosCard}
                  />
                  <DepositRow
                    title={t('cashUp.cashupTableLeft.tableRow.inStoreCard')}
                    depositCount={dcDepositNonSlickPosCarCount}
                    refundedCount={dcDepositNonSlickPosCarRefundedCount}
                    netValue={dcDepositNonSlickPosCard}
                  />
                  <DepositRow
                    title={t('cashUp.cashupTableLeft.tableRow.onlineCard')}
                    depositCount={dcDepositOnlineCount}
                    refundedCount={dcRefundOnlineCount}
                    netValue={dcDepositOnlineCard}
                  />
                  <DepositRow
                    title={t('cashUp.cashupTableLeft.tableRow.other')}
                    depositCount={dcDepositOtherCount}
                    refundedCount={dcRefundOtherCount}
                    netValue={dcDepositOther}
                  />
                  <DepositRow
                    title={t('common.total')}
                    depositCount={dcDepositTotalCount}
                    refundedCount={dcRefundTotalCount}
                    netValue={dcDepositTaken}
                    isTotal={true}
                  />
                </ContainmentTable>
              </Permitted>

              <ContainmentTable id="tblVoucherSold">
                <VoucherHeader t={t} />
                <VoucherRow
                  title={t('cashUp.cashupTableLeft.tableRow.cash')}
                  count={dcVoucherCashCount}
                  netValue={dcVouchersCash}
                />
                <VoucherRow
                  title={t('cashUp.cashupTableLeft.tableRow.inStoreSlickCard')}
                  count={dcVoucherCardCount}
                  netValue={dcVoucherCard}
                />
                <VoucherRow
                  title={t('cashUp.cashupTableLeft.tableRow.inStoreCard')}
                  count={dcVoucherNonSlickPosCardCount}
                  netValue={dcVoucherNonSlickPosCard}
                />
                <VoucherRow
                  title={t('cashUp.cashupTableLeft.tableRow.onlineCard')}
                  count={dcVoucherOnlineCount}
                  netValue={dcVouchersOnline}
                />
                <VoucherRow
                  title={t('cashUp.cashupTableLeft.tableRow.other')}
                  count={dcVoucherOtherCount}
                  netValue={cVoucherOther}
                />
                <VoucherRow
                  title={t('common.total')}
                  count={dcVoucherTotalCount}
                  netValue={dSumVoucherNetValueAll}
                  isTotal={true}
                />
              </ContainmentTable>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

const mapStateToProps = (state) => ({
  apiData: selectCashupApiData(state),
  voucherSoldList: selectCashupSoldVouchers(state),
  depositsLogs: selectCashupDepositLogs(state),
});

const CashupTableLeftHoc = connect(mapStateToProps)(CashupTableLeft);

// this extra export is needed for tests that provide their own mock for connect()
export { CashupTableLeft as CashupTableLeft4Test };

export default CashupTableLeftHoc;
